/**
 * HomeShortsCarousel — Section « Shorts du moment » pour la page d'accueil.
 *
 * Format : carrousel horizontal scrollable avec cartes verticales 9/16.
 * Chaque carte affiche la miniature (poster YouTube ou image statique),
 * le badge thème coloré, le titre, l'auteur et un compteur de vues.
 * Au clic : navigation vers `/shorts?s={id}` qui ouvre directement sur ce short.
 *
 * Sur desktop : 4-5 cartes visibles, scroll horizontal natif + chevrons.
 * Sur mobile : 2 cartes visibles, swipe horizontal natif.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clapperboard, Eye, PlayCircle, Sparkles } from 'lucide-react';
import api from '../api';
import { Button } from './ui/button';

const THEME_COLORS = {
  equipement: '#2563eb',
  etm: '#f59e0b',
  plateau: '#cf2031',
  circulation: '#7c3aed',
  trajectoires: '#059669',
  freinage: '#dc2626',
  'angles-morts': '#0891b2',
  interfiles: '#9333ea',
  meteo: '#0369a1',
  mentale: '#be185d',
  'gestes-signaleur': '#ea580c',
  autre: '#6b7280',
};

const KIND_LABEL = {
  official: 'Officiel',
  quiz: 'Quiz',
  editorial: 'C2R',
  member: 'Communauté',
};

function formatViews(n) {
  if (!n || n < 1) return '0';
  if (n < 1000) return `${n}`;
  if (n < 10000) return `${(n / 1000).toFixed(1).replace('.0', '')}k`;
  if (n < 1e6) return `${Math.floor(n / 1000)}k`;
  return `${(n / 1e6).toFixed(1).replace('.0', '')}M`;
}

function posterFor(short) {
  if (short.poster_url) return short.poster_url;
  if (short.youtube_id) return `https://i.ytimg.com/vi/${short.youtube_id}/hqdefault.jpg`;
  return null;
}

export default function HomeShortsCarousel({ limit = 10 }) {
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const feed = await api.shortsFeed();
        if (!cancelled) setShorts((feed || []).slice(0, limit));
      } catch {
        /* Section cachée en cas d'erreur */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  const updateScrollButtons = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    updateScrollButtons();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [shorts.length]);

  const scrollBy = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.firstChild?.offsetWidth || 200;
    el.scrollBy({ left: direction * (cardWidth * 2 + 24), behavior: 'smooth' });
  };

  // Masquer la section si rien à montrer (évite le squelette vide)
  if (!loading && shorts.length === 0) return null;

  return (
    <section
      className="relative w-full py-10 md:py-14"
      data-testid="home-shorts-carousel"
      aria-labelledby="home-shorts-title"
    >
      {/* En-tête */}
      <div className="max-w-7xl mx-auto px-4 flex items-end justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--primary))] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--primary))]" />
            </span>
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Shorts du moment · Vidéos verticales
            </span>
          </div>
          <h2
            id="home-shorts-title"
            className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            <Clapperboard className="w-6 h-6 text-[hsl(var(--primary))]" />
            Shorts du moment
          </h2>
          <p className="text-sm text-foreground/60 mt-1 italic">
            Mini-vidéos moto : équipement, sécurité, interfiles, plateau…
          </p>
        </div>
        <Link to="/shorts">
          <Button variant="outline" size="sm" className="whitespace-nowrap" data-testid="home-shorts-see-all">
            Tous les shorts →
          </Button>
        </Link>
      </div>

      {/* Carrousel */}
      <div className="relative max-w-7xl mx-auto">
        {/* Chevrons desktop */}
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Faire défiler vers la gauche"
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background shadow-lg border border-border/50 items-center justify-center hover:bg-card transition-colors"
            data-testid="home-shorts-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Faire défiler vers la droite"
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background shadow-lg border border-border/50 items-center justify-center hover:bg-card transition-colors"
            data-testid="home-shorts-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Fil scrollable */}
        <div
          ref={scrollerRef}
          className="flex gap-3 md:gap-4 overflow-x-auto overflow-y-hidden scroll-smooth px-4 pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`
            [data-testid="home-shorts-carousel"] .flex::-webkit-scrollbar { display: none; }
          `}</style>

          {loading && Array.from({ length: 4 }).map((_, i) => (
            <div
              key={`skel-${i}`}
              className="flex-shrink-0 w-[160px] sm:w-[180px] md:w-[200px] rounded-xl bg-foreground/5 animate-pulse snap-start"
              style={{ aspectRatio: '9 / 16' }}
            />
          ))}

          {!loading && shorts.map((s) => (
            <ShortCard key={s.id} short={s} />
          ))}

          {!loading && shorts.length > 0 && (
            <Link
              to="/shorts"
              className="flex-shrink-0 w-[160px] sm:w-[180px] md:w-[200px] rounded-xl border-2 border-dashed border-border/50 bg-foreground/[0.02] flex flex-col items-center justify-center gap-2 text-foreground/60 hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))] transition-colors snap-start"
              style={{ aspectRatio: '9 / 16' }}
              data-testid="home-shorts-card-see-all"
            >
              <PlayCircle className="w-10 h-10" />
              <span className="text-xs font-black uppercase tracking-widest text-center px-3">
                Voir tous les shorts
              </span>
            </Link>
          )}
        </div>
      </div>

      {/* CTA mobile sous le carrousel */}
      <div className="md:hidden max-w-7xl mx-auto px-4 mt-2 flex items-center gap-2 text-[11px] text-foreground/55">
        <Sparkles className="w-3 h-3 text-[hsl(var(--primary))]" />
        Partagez vos propres astuces : <Link to="/shorts/proposer" className="underline font-bold ml-1">proposer un short</Link>
      </div>
    </section>
  );
}

/**
 * Carte individuelle d'un short dans le carrousel.
 * Format vertical 9/16, poster + overlay bas, badge thème, compteur vues.
 */
function ShortCard({ short }) {
  const poster = posterFor(short);
  const themeColor = THEME_COLORS[short.theme] || THEME_COLORS.autre;
  const kindLabel = KIND_LABEL[short.kind] || 'C2R';
  return (
    <Link
      to={`/shorts?s=${encodeURIComponent(short.id)}`}
      className="group relative flex-shrink-0 w-[160px] sm:w-[180px] md:w-[200px] rounded-xl overflow-hidden bg-black shadow-md hover:shadow-xl hover:scale-[1.02] transition-all snap-start"
      style={{ aspectRatio: '9 / 16' }}
      data-testid={`home-shorts-card-${short.id}`}
      aria-label={`Lire le short : ${short.title}`}
    >
      {/* Miniature */}
      {poster ? (
        <img
          src={poster}
          alt={short.title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            if (short.youtube_id && e.currentTarget.src.includes('maxres')) {
              e.currentTarget.src = `https://i.ytimg.com/vi/${short.youtube_id}/hqdefault.jpg`;
            }
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-900 flex items-center justify-center text-white/40 text-xs">
          Pas d'aperçu
        </div>
      )}

      {/* Icône play qui apparaît au hover */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity">
        <PlayCircle className="w-12 h-12 text-white drop-shadow-lg" strokeWidth={1.5} />
      </div>

      {/* Dégradé bas pour lisibilité */}
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/95 via-black/55 to-transparent pointer-events-none" />

      {/* Badge thème + kind haut-gauche */}
      <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white shadow"
          style={{ background: themeColor }}
        >
          {short.theme_label}
        </span>
        {short.kind === 'official' && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white bg-blue-600 shadow">
            {kindLabel}
          </span>
        )}
      </div>

      {/* Compteur vues bas-droite */}
      <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/60 backdrop-blur-md text-white text-[10px] font-bold">
        <Eye className="w-2.5 h-2.5" />
        {formatViews(short.views || 0)}
      </div>

      {/* Titre bas */}
      <div className="absolute inset-x-0 bottom-0 p-2.5 pointer-events-none">
        <h3 className="text-white text-[13px] font-extrabold leading-tight line-clamp-2 drop-shadow mb-6">
          {short.title}
        </h3>
      </div>
    </Link>
  );
}
